// @ts-ignore - Type import from vue-router causes conflicts with Ionic Vue Router
import type { Router, RouteLocationNormalized, NavigationGuardNext } from 'vue-router';
import { getSEOManager } from './index';
import { SEORouteConfig } from './types';

/**
 * Sets up router-level SEO functionality by adding navigation guards
 * @param router Vue Router instance
 */
export function setupRouterSEO(router: Router): void {
    const seoManager = getSEOManager();

    if (!seoManager) {
        console.warn('SEO Manager not found. Router SEO functionality will be disabled.');
        return;
    }

    // Add navigation guard for SEO updates
    router.beforeEach(seoNavigationGuard);
}

/**
 * Navigation guard that applies SEO configuration based on route meta
 * @param to Destination route
 * @param from Source route
 * @param next Navigation callback
 */
export async function seoNavigationGuard(to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext): Promise<void> {
    const seoManager = getSEOManager();

    if (!seoManager) {
        next();
        return;
    }

    // Check if SEO manager is configured for native platform (it will skip operations internally)
    // The SEO manager itself handles the native platform check, so we can proceed normally

    try {
        // Extract SEO configuration from route meta
        const seoConfig = extractSEOConfig(to);

        if (seoConfig) {
            // Apply the SEO configuration (SEOManager will skip if on native platform)
            await applySEOConfig(seoConfig, to, seoManager);
        }
    } catch (error) {
        console.error('Error applying SEO configuration:', error);
    }

    next();
}

/**
 * Extracts and processes SEO configuration from route meta
 * @param route Current route
 * @returns Processed SEO configuration or null
 */
function extractSEOConfig(route: RouteLocationNormalized): SEORouteConfig | null {
    let seoConfig = route.meta?.seo as SEORouteConfig;

    if (!seoConfig) {
        return null;
    }

    // Handle inheritance from parent routes
    if (seoConfig.inheritFromParent && route.matched) {
        seoConfig = inheritFromParents(route, seoConfig);
    }

    return seoConfig;
}

/**
 * Inherits SEO configuration from parent routes
 * @param route Current route
 * @param childConfig Child route SEO config
 * @returns Merged SEO configuration
 */
function inheritFromParents(route: RouteLocationNormalized, childConfig: SEORouteConfig): SEORouteConfig {
    const mergedConfig = { ...childConfig };

    // Process matched routes from parent to child (excluding the current route)
    for (let i = 0; i < route.matched.length - 1; i++) {
        const parentMeta = route.matched[i].meta;
        const parentSeo = parentMeta?.seo as SEORouteConfig;

        if (parentSeo) {
            // Merge parent properties that are not already defined in child
            Object.keys(parentSeo).forEach((key) => {
                if (key !== 'inheritFromParent' && !(key in mergedConfig)) {
                    (mergedConfig as any)[key] = (parentSeo as any)[key];
                }
            });
        }
    }

    return mergedConfig;
}

/**
 * Applies SEO configuration using the SEO manager
 * @param config SEO configuration
 * @param route Current route for dynamic values
 * @param seoManager SEO manager instance
 */
async function applySEOConfig(config: SEORouteConfig, route: RouteLocationNormalized, seoManager: any): Promise<void> {
    const processedConfig: any = {};

    // Process each configuration property
    Object.keys(config).forEach((key) => {
        if (key === 'inheritFromParent') {
            return; // Skip this meta property
        }

        const value = (config as any)[key];

        if (typeof value === 'function') {
            // Call function with route context
            processedConfig[key] = value(route);
        } else {
            // Use static value
            processedConfig[key] = value;
        }
    });

    // Handle structured data separately
    if (processedConfig.structuredData) {
        processedConfig.jsonLd = processedConfig.structuredData;
        delete processedConfig.structuredData;
    }

    // Apply the processed configuration
    seoManager.applyOptions(processedConfig);

    // Update current page URL after route navigation
    seoManager.updateCurrentPageUrl();
}
